import { CircleCheck, CreditCard, Lock, ShoppingCart, WifiOff, Wrench } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  errorView,
  useMaintenanceSetMutation,
  useMaintenanceWorkplacesQuery,
  type MaintenanceUpdate,
  type MaintenanceWorkplace,
} from '@krp/client-runtime'
import { Button } from '@krp/ui-kit'

import '../../app/maintenanceGate.css'
import { useAppSelector } from '../../app/hooks'

/** Как часто пульт перечитывает список сам, помимо событий живого канала: так «нет связи» у молчащей кассы появляется вовремя. */
export const WORKPLACES_POLL_MS = 10_000

/** Состояние кассы словами и значком — не только цветом (CLAUDE.md §7). */
function stateOf(workplace: MaintenanceWorkplace): { icon: ReactNode; text: string } {
  if (workplace.silent) return { icon: <WifiOff size={16} aria-hidden="true" />, text: 'Нет связи' }
  switch (workplace.state) {
    case 'paying':
      return { icon: <CreditCard size={16} aria-hidden="true" />, text: 'Идёт оплата / пробитие чека' }
    case 'cart':
      return { icon: <ShoppingCart size={16} aria-hidden="true" />, text: 'Набирается корзина' }
    case 'locked':
      return { icon: <Lock size={16} aria-hidden="true" />, text: 'Заблокирована' }
    case 'idle':
      return { icon: <CircleCheck size={16} aria-hidden="true" />, text: 'Свободна, корзина пуста' }
  }
}

const timeOf = (iso: string | null): string => {
  if (iso === null || iso === '') return ''
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? '' : at.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}

/** Итог пульта одной фразой: можно ли начинать обновление. */
function summaryOf(active: boolean, workplaces: readonly MaintenanceWorkplace[]): string {
  const live = workplaces.filter((workplace) => !workplace.silent)
  const busy = live.filter((workplace) => workplace.state === 'paying' || workplace.state === 'cart').length
  const silent = workplaces.length - live.length
  const silentText = silent > 0 ? ` Без связи: ${silent} — проверьте эти кассы сами.` : ''
  if (!active) {
    if (workplaces.length === 0) return 'Открытых касс нет.'
    return `Открыто касс: ${workplaces.length}, продажа идёт на ${busy}.${silentText}`
  }
  if (busy > 0) return `Ждём завершения чеков: продажа идёт ещё на ${busy} из ${live.length}. Обновление пока не начинайте.${silentText}`
  return `Все кассы на связи заблокированы — можно начинать обновление.${silentText}`
}

export interface MaintenancePanelProps {
  active: boolean
  message: string | null
  since: string | null
  workplaces: readonly MaintenanceWorkplace[]
  /** Список ещё не пришёл. */
  loading: boolean
  /** Список не прочитан: слова сервера. */
  error: string | null
  busy: boolean
  /** Включение или выключение не прошло: слова сервера. */
  applyError: string | null
  onApply: (update: MaintenanceUpdate) => void
}

/**
 * Пульт обновления касс (RP-MNT), окно в «Настройках»: какие РМК открыты, кто за ними и что на них сейчас — свободна, набирается
 * корзина, идёт оплата или пробитие, заблокирована, нет связи; включить или выключить режим обновления. Состояние каждой кассы
 * приходит от неё самой (её пульс), поэтому администратор видит, когда все кассы заперты и обновление можно начинать.
 */
export function MaintenancePanel({ active, message, since, workplaces, loading, error, busy, applyError, onApply }: MaintenancePanelProps) {
  const [note, setNote] = useState(message ?? '')
  return (
    <section className="maintenance-switch" aria-label="Обновление касс">
      <p className="maintenance-switch__state">
        {active ? `Режим обновления включён${since !== null ? ` с ${timeOf(since)}` : ''}: новые продажи закрыты.` : 'Режим обновления выключен: кассы работают как обычно.'}
      </p>
      <p className="maintenance-switch__summary" role="status" aria-live="polite">
        {loading ? 'Читаем список открытых касс…' : error !== null ? `Список касс не прочитан: ${error}` : summaryOf(active, workplaces)}
      </p>
      {workplaces.length > 0 && (
        <ul className="maintenance-board" aria-label="Открытые кассы">
          {workplaces.map((workplace) => {
            const { icon, text } = stateOf(workplace)
            return (
              <li key={workplace.workplaceId} className="maintenance-board__row" data-state={workplace.silent ? 'silent' : workplace.state}>
                <span className="maintenance-board__place">
                  <b>{workplace.label}</b>
                  <small>{workplace.person}</small>
                </span>
                <span className="maintenance-board__state">
                  {icon} {text}
                  {!workplace.silent && workplace.stateSince !== '' && <small> с {timeOf(workplace.stateSince)}</small>}
                </span>
              </li>
            )
          })}
        </ul>
      )}
      {!active && (
        <label className="maintenance-switch__field">
          <span>Сообщение кассирам (необязательно)</span>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={2}
            maxLength={200}
            placeholder="Например: обновление системы, вернёмся через 10 минут"
          />
        </label>
      )}
      <Button
        variant="primary"
        busy={busy}
        icon={<Wrench size={16} aria-hidden="true" />}
        onClick={() => onApply(active ? { active: false, message: null } : { active: true, message: note.trim() === '' ? null : note.trim() })}
        actionId="platform.maintenance.apply"
      >
        {active ? 'Разблокировать кассы' : 'Заблокировать кассы'}
      </Button>
      {applyError !== null && (
        <p className="maintenance-switch__error" role="alert">
          Не получилось: {applyError}
        </p>
      )}
      <p className="maintenance-switch__hint">
        Касса, на которой набрана корзина или идёт оплата, не прерывается: кассир доводит чек, и касса запирается сама. Выключение режима — а
        также возврат сервера после обновления — возвращает кассы к работе.
      </p>
    </section>
  )
}

/** Окно «Обновление касс»: режим из среза `maintenance`, список касс — с сервера (событие живого канала и опрос раз в 10 с). */
export function MaintenanceBinding() {
  const active = useAppSelector((state) => state.maintenance.active)
  const message = useAppSelector((state) => state.maintenance.message)
  const since = useAppSelector((state) => state.maintenance.since)
  const workplaces = useMaintenanceWorkplacesQuery(undefined, { pollingInterval: WORKPLACES_POLL_MS, refetchOnMountOrArgChange: true })
  const [apply, applied] = useMaintenanceSetMutation()
  return (
    <MaintenancePanel
      active={active}
      message={message}
      since={since}
      workplaces={workplaces.data ?? []}
      loading={workplaces.isLoading}
      error={workplaces.isError ? errorView(workplaces.error).message : null}
      busy={applied.isLoading}
      applyError={applied.isError ? errorView(applied.error).message : null}
      onApply={(update) => void apply(update)}
    />
  )
}
